import { TypedDocumentNode, gql } from '@apollo/client';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { generatePath, useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import {
  CardQuality,
  FootballManagerTaskState,
  Rarity,
  Sport,
} from '@sorare/core/src/__generated__/globalTypes';
import { namedOperations } from '@sorare/core/src/__generated__/operations';
import commonEssenceBig from '@sorare/core/src/assets/rewards/common-essence-big.png';
import commonEssence from '@sorare/core/src/assets/rewards/common-essence.png';
import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { ButtonBase } from '@sorare/core/src/atoms/buttons/ButtonBase';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { Scrollable } from '@sorare/core/src/atoms/navigation/Scrollable';
import {
  BodyS,
  DisplayS,
  LabelL,
  LabelM,
  LabelS,
} from '@sorare/core/src/atoms/typography';
import { ResponsiveImg } from '@sorare/core/src/atoms/ui/ResponsiveImg';
import { NBA_REWARD_BOX_ID } from '@sorare/core/src/constants/__generated__/routes';
import { TimeLeft } from '@sorare/core/src/contexts/ticker/TimeLeft';
import { idFromObject } from '@sorare/core/src/gql/idFromObject';
import { useIsMobileApp } from '@sorare/core/src/hooks/useIsMobileApp';
import { glossary } from '@sorare/core/src/lib/glossary';
import { qualityNames } from '@sorare/core/src/lib/players';
import { Color } from '@sorare/core/src/style/types';

import { useClaimManagerTask } from '@sorare/gameplay/src/hooks/useClaimManagerTask';

import packCommentTier0 from 'assets/packs/pack-common-tier-0.png';
import packCommentTier1 from 'assets/packs/pack-common-tier-1.png';
import packCommentTier2 from 'assets/packs/pack-common-tier-2.png';
import packCommentTier3 from 'assets/packs/pack-common-tier-3.png';
import packCommentTier4 from 'assets/packs/pack-common-tier-4.png';
import packCommentTier5 from 'assets/packs/pack-common-tier-5.png';

import { DailyReward, DailyRewardBlock, RewardWrapper } from '../DailyReward';
import { FirstWeekClaim_nbaManagerTask } from './__generated__/index.graphql';

const Root = styled(Vertical)`
  padding: 0 var(--triple-unit);
  max-width: 100%;
  flex: 1;
`;

const FeaturedRewardWrapped = styled(Horizontal).attrs({
  gap: 0,
  center: true,
})`
  flex: 1;
`;
const DailyRewardWrapper = styled.div`
  padding: var(--unit) 0;
`;

const FeaturedRewardImg = styled(ResponsiveImg)`
  max-width: 270px;
  max-height: min(34vh, 300px);
  height: 100%;
  object-fit: contain;
`;

const CardBackWrapper = styled.div`
  & > * {
    height: var(--quadruple-unit);
  }
`;

type rewardProp = {
  backgroundColor: Color;
  buttonColor: Color;
  borderColor?: Color;
  borderWidth?: number;
};
const rewardProps = (
  { rewardConfig }: FirstWeekClaim_nbaManagerTask,
  { isFeatured }: { isFeatured: boolean }
): rewardProp => {
  const isBox = !!rewardConfig.probabilisticBundleConfiguration;

  if (isBox) {
    return {
      backgroundColor: 'rgba(var(--c-rgb-green-800), 0.17)',
      buttonColor: '#1E312D' as Color,
      borderColor: 'rgba(var(--c-rgb-green-800), 1)',
      borderWidth: 1,
    };
  }

  if (isFeatured) {
    return {
      backgroundColor: 'rgba(var(--c-rgb-blue-300), 0.16)',
      buttonColor: 'rgba(var(--c-rgb-white), 0.17)',
      borderColor: 'var(--c-blue-400)',
      borderWidth: 2,
    };
  }

  return {
    backgroundColor: 'rgba(var(--c-rgb-white), 0.07)',
    buttonColor: 'rgba(var(--c-rgb-white), 0.17)',
    borderColor: 'rgba(var(--c-rgb-white), 0.17)',
    borderWidth: 1,
  };
};

const qualityPackImage: { [key in CardQuality]: string } = {
  TIER_0: packCommentTier0,
  TIER_1: packCommentTier1,
  TIER_2: packCommentTier2,
  TIER_3: packCommentTier3,
  TIER_4: packCommentTier4,
  TIER_5: packCommentTier5,
};

type Card = {
  slug: string;
  rarityTyped: Rarity;
  pictureUrl: string | null;
  anyPlayer: {
    slug: string;
    displayName: string;
  };
};

type Props = {
  dailyClaimTasks: FirstWeekClaim_nbaManagerTask[];
  onClose: () => void;
  onClaimCard: ({ ftue, card }: { ftue: boolean; card: Card }) => void;
};
export const FirstWeekClaim = ({
  dailyClaimTasks,
  onClose,
  onClaimCard,
}: Props) => {
  const navigate = useNavigate();
  const [claim] = useClaimManagerTask();
  const { isIosApp } = useIsMobileApp();
  const [indexToScroll, setIndexToScroll] = useState(
    dailyClaimTasks.findIndex(
      ({ aasmState }) => aasmState !== FootballManagerTaskState.CLAIMED
    )
  );

  const readyTaskIndex = dailyClaimTasks.findIndex(
    ({ aasmState }) => aasmState === FootballManagerTaskState.READY
  );
  const readyTask =
    (readyTaskIndex >= 0 && dailyClaimTasks[readyTaskIndex]) || undefined;

  const completedTaskIndex = dailyClaimTasks.findIndex(
    ({ aasmState }) => aasmState === FootballManagerTaskState.COMPLETED
  );
  const completedTask =
    completedTaskIndex >= 0 && dailyClaimTasks[completedTaskIndex];

  const featuredRewardIndex =
    completedTaskIndex >= 0 ? completedTaskIndex : readyTaskIndex;

  const featuredReward = completedTask || readyTask || undefined;

  const isCardReward = Boolean(featuredReward?.rewardConfig.cards?.[0]);

  let featureRewardImage = '';
  if (isCardReward && featuredReward?.rewardConfig.cards[0]?.quality) {
    featureRewardImage =
      qualityPackImage[
        featuredReward.rewardConfig.cards[0]?.quality || CardQuality.TIER_3
      ];
  } else if (
    featuredReward?.rewardConfig.probabilisticBundleConfiguration?.pictureUrl
  ) {
    featureRewardImage =
      featuredReward.rewardConfig.probabilisticBundleConfiguration.pictureUrl;
  } else if (featuredReward?.rewardConfig.cardShards?.length) {
    featureRewardImage = commonEssenceBig;
  }

  const isFeaturedRewardLocked =
    !featuredReward ||
    featuredReward.aasmState !== FootballManagerTaskState.COMPLETED;

  const onClaim = async ({
    managerTaskId,
    ftue,
  }: {
    managerTaskId: string;
    ftue: boolean;
  }) => {
    const { data } = await claim({
      refetchQueries: [namedOperations.Query.NBADAilyClaimDialogQuery],
      variables: {
        sport: Sport.NBA,
        input: {
          footballManagerTaskId: managerTaskId,
        },
      },
    });
    const cards = data?.claimFootballManagerTask?.footballManagerTask?.cards;
    if (cards && cards.length > 0)
      onClaimCard({
        ftue,
        card: cards[0],
      });

    const probabilisticBundle =
      data?.claimFootballManagerTask?.footballManagerTask?.probabilisticBundle;

    if (probabilisticBundle) {
      navigate(
        generatePath(NBA_REWARD_BOX_ID, {
          id: idFromObject(probabilisticBundle.id),
        })
      );
    }

    onClose();
  };

  return (
    <Root gap={3} center>
      <Vertical center>
        <DisplayS brand className="text-center">
          <FormattedMessage
            id="ClaimArenaTicketDialog.title"
            defaultMessage="Daily Claim"
          />
        </DisplayS>
        <LabelM as="span" color="var(--c-nd-600)">
          <FormattedMessage
            id="NBA.DailyClaim.dialog.subtitle"
            defaultMessage="Claim a new reward every 24 hours"
          />
        </LabelM>
      </Vertical>
      <Scrollable
        itemToDisplay={4}
        indexToScroll={indexToScroll}
        onVisibleItemsChanged={items => setIndexToScroll(items[0])}
        withMask
      >
        {dailyClaimTasks.map((task, i) => {
          const isCompleted =
            task.aasmState === FootballManagerTaskState.COMPLETED;
          const isLocked =
            task.aasmState === FootballManagerTaskState.ASSIGNED ||
            (task.aasmState === FootballManagerTaskState.READY &&
              readyTask?.id !== task.id) ||
            (task.aasmState === FootballManagerTaskState.CLAIMED &&
              readyTask?.id !== task.id);

          const { backgroundColor, borderColor, buttonColor, borderWidth } =
            rewardProps(task, { isFeatured: task.id === featuredReward?.id });

          return (
            <DailyRewardWrapper key={task.id}>
              <DailyReward
                isBox={!!task.rewardConfig.probabilisticBundleConfiguration}
                disabled={isLocked}
                readyToClaim={isCompleted}
                buttonColor={buttonColor}
                backgroundColor={backgroundColor}
                borderColor={borderColor}
                borderWidth={borderWidth}
                footer={
                  <BodyS as="span" color="var(--c-nd-700)" bold>
                    {task.readyAt &&
                    task.aasmState === FootballManagerTaskState.READY ? (
                      <TimeLeft time={task.readyAt} />
                    ) : (
                      <FormattedMessage
                        id="ClaimArenaTicketDialog.dailyClaimTasks.dayI"
                        defaultMessage="Day {i}"
                        values={{ i: i + 1 }}
                      />
                    )}
                  </BodyS>
                }
              >
                <DailyRewardBlock>
                  {task.rewardConfig.cards.length > 0 && (
                    <RewardWrapper>
                      {task.rewardConfig.cards.map((card, j) => {
                        return (
                          // eslint-disable-next-line react/no-array-index-key
                          <Vertical key={`${task.id}_${j}`} center>
                            <CardBackWrapper>
                              <img
                                src={
                                  qualityPackImage[
                                    card.quality || CardQuality.TIER_3
                                  ]
                                }
                                alt=""
                              />
                            </CardBackWrapper>
                            <LabelS
                              bold
                              className="text-center whitespace-nowrap"
                            >
                              {`x1 ${qualityNames[card.quality || CardQuality.TIER_3]}`}
                            </LabelS>
                          </Vertical>
                        );
                      })}
                    </RewardWrapper>
                  )}
                  {task.rewardConfig.cardShards?.[0] && (
                    <RewardWrapper>
                      <Vertical center>
                        <img height={36} src={commonEssence} alt="" />
                        <LabelS bold className="text-center whitespace-nowrap">
                          {`x${task.rewardConfig.cardShards[0].config.quantity}`}
                        </LabelS>
                      </Vertical>
                    </RewardWrapper>
                  )}
                  {task.rewardConfig.probabilisticBundleConfiguration && (
                    <RewardWrapper>
                      <img
                        width={36}
                        height={36}
                        src={
                          task.rewardConfig.probabilisticBundleConfiguration
                            .pictureUrl
                        }
                        alt=""
                      />
                    </RewardWrapper>
                  )}
                </DailyRewardBlock>
              </DailyReward>
            </DailyRewardWrapper>
          );
        })}
      </Scrollable>
      <FeaturedRewardWrapped>
        {featuredReward && (
          <ButtonBase
            disabled={
              featuredReward.aasmState !== FootballManagerTaskState.COMPLETED
            }
            onClick={async () =>
              await onClaim({
                managerTaskId: featuredReward?.id,
                ftue: featuredRewardIndex === 0,
              })
            }
          >
            <Vertical center gap={2}>
              <FeaturedRewardImg src={featureRewardImage} alt="" />
              {featuredReward?.rewardConfig.cardShards?.[0] && (
                <LabelL bold className="text-center whitespace-nowrap">
                  {`x${featuredReward?.rewardConfig.cardShards[0].config.quantity}`}
                </LabelL>
              )}
            </Vertical>
          </ButtonBase>
        )}
      </FeaturedRewardWrapped>
      {featuredReward && (
        <>
          {isIosApp ? (
            <Button
              fullWidth
              size="large"
              color="secondary"
              disabled={isFeaturedRewardLocked}
              onClick={async () =>
                await onClaim({
                  managerTaskId: featuredReward?.id,
                  ftue: featuredRewardIndex === 0,
                })
              }
            >
              <FormattedMessage
                id="S3Onboarding.StarterPack.cta"
                defaultMessage="Tap to reveal"
              />
            </Button>
          ) : (
            <Button
              fullWidth
              size="large"
              color="secondary"
              onClick={
                isFeaturedRewardLocked
                  ? () => onClose()
                  : async () =>
                      await onClaim({
                        managerTaskId: featuredReward?.id,
                        ftue: featuredRewardIndex === 0,
                      })
              }
            >
              {isFeaturedRewardLocked ? (
                <FormattedMessage {...glossary.continue} />
              ) : (
                <FormattedMessage
                  id="S3Onboarding.StarterPack.cta"
                  defaultMessage="Tap to reveal"
                />
              )}
            </Button>
          )}
        </>
      )}
    </Root>
  );
};

FirstWeekClaim.fragments = {
  nbaManagerTask: gql`
    fragment FirstWeekClaim_nbaManagerTask on FootballManagerTask {
      id
      aasmState
      label
      readyAt
      rewardConfig {
        probabilisticBundleConfiguration {
          id
          pictureUrl
          quality
        }
        cardShards {
          id
          config {
            id
            quantity
            rarity
          }
        }
        cards {
          rarity
          title
          description
          quality
        }
      }
    }
  ` as TypedDocumentNode<FirstWeekClaim_nbaManagerTask>,
};
